import { BoundSelectField, Button, Css, FormLines, StaticField, useModal, useSnackbar } from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import startCase from "lodash/startCase";
import { Observer } from "mobx-react";
import { useHistory, useLocation, useParams } from "react-router";
import { addEntityParam, createInvoiceTermsUrl } from "src/RouteUrls";
import { emptyCellDash } from "src/components";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  CommunityType,
  CostClassificationType,
  InvoiceScheduleAmountType,
  InvoiceScheduleTriggerType,
  LotType,
  SaveInvoiceScheduleTemplateInput,
} from "src/generated/graphqlTypes";
import { useToggle } from "src/hooks";
import {
  InvoiceTermsForm_InvoiceScheduleTemplateFragment,
  useInvoiceTermsFormMetadataQuery,
  useInvoiceTermsFormQuery,
  useSaveInvoiceTermsMutation,
} from "src/routes/billing/invoice-terms/components/InvoiceTermsForm.generated";
import { PageHeader } from "src/routes/layout/PageHeader";
import { IdOrAddParams } from "src/routes/routesDef";
import { queriesResult } from "src/utils";
import { DateOnly, DateTime, formatWithYear } from "src/utils/dates";
import { InvoiceTermsItems } from "./InvoiceTermsItems";

const DAYS_IN_MONTH = 31;

type InvoiceTermsFormState = { name: string; lotType: LotType; customerId: string; communityType?: CommunityType };

type InvoiceTermsFormProps = { invoiceScheduleTemplateId?: string };

export function InvoiceTermsForm({ invoiceScheduleTemplateId }: InvoiceTermsFormProps) {
  const paramsIdOrAdd = useParams<IdOrAddParams>().idOrAdd;
  const idOrAdd = invoiceScheduleTemplateId ?? paramsIdOrAdd;
  const isNew = idOrAdd === addEntityParam;
  const metadataQuery = useInvoiceTermsFormMetadataQuery({
    variables: { filter: { isFromInvoiceAutomation: true } },
    fetchPolicy: "cache-first",
  });
  const invoiceTermsQuery = useInvoiceTermsFormQuery({ variables: { id: idOrAdd }, skip: isNew });

  return queriesResult([metadataQuery, invoiceTermsQuery] as const, {
    data: (metadata, invoiceTermData) => {
      return (
        <InvoiceTermsFormView
          customers={metadata.customers}
          regions={metadata.regions}
          globalPlanMilestones={metadata.globalPlanMilestones.entities}
          invoiceTerm={invoiceTermData?.invoiceScheduleTemplate}
        />
      );
    },
  });
}

type InvoiceTermsFormViewProps = {
  customers: NamedFragment[];
  regions: NamedFragment[];
  globalPlanMilestones: NamedFragment[];
  invoiceTerm: InvoiceTermsForm_InvoiceScheduleTemplateFragment | undefined;
};

function InvoiceTermsFormView({ customers, regions, globalPlanMilestones, invoiceTerm }: InvoiceTermsFormViewProps) {
  const history = useHistory();
  const { inModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const [saveInvoiceTerm] = useSaveInvoiceTermsMutation();
  const location = useLocation<InvoiceTermsFormState>();
  const [readOnly, toggleReadonly] = useToggle(!!invoiceTerm);
  const formState = useFormState({
    config: formConfig,
    init: { input: invoiceTerm ?? location.state, map: mapToForm },
  });
  const daysOfMonth = Array.from({ length: DAYS_IN_MONTH }, (_, i) => i + 1);

  return (
    <Observer>
      {() => (
        <>
          {!inModal && (
            <PageHeader
              title="Create Invoice Terms"
              right={
                !readOnly ? (
                  <>
                    <Button variant="quaternary" onClick={createInvoiceTermsUrl()} label="Cancel" />
                    <Button variant="primary" disabled={!formState.valid} onClick={toggleReadonly} label="Review" />
                  </>
                ) : (
                  <>
                    <Button variant="tertiary" onClick={toggleReadonly} label="Edit" />
                    <Button
                      variant="primary"
                      onClick={async () => {
                        const { createdAt, ...input } = formState.value;
                        const { data } = await saveInvoiceTerm({ variables: { input } });
                        if (data) {
                          triggerNotice({
                            message: invoiceTerm
                              ? "Invoice Terms successfully updated"
                              : `New Invoice Terms: ${data.saveInvoiceScheduleTemplate.invoiceScheduleTemplate.name} created`,
                            icon: "success",
                          });
                          history.push(createInvoiceTermsUrl());
                        }
                      }}
                      label={invoiceTerm ? "save" : "Create"}
                      disabled={!formState.dirty}
                    />
                  </>
                )
              }
            />
          )}
          <div css={Css.mt2.maxw("60%").mxa.br12.bgWhite.p4.if(!!inModal).maxw100.p1.$}>
            <section>
              {!readOnly ? (
                <div css={Css.xl2.mb3.$}>New Invoice Terms</div>
              ) : (
                <>
                  <div css={Css.xl2.$}>{formState.name.value}</div>
                  <div css={Css.sm.mt1.gray700.$}>
                    Created on {formatWithYear(new DateOnly(formState.createdAt.value))}
                  </div>
                  <div css={Css.mt4.mb3.lg.$}>Overview</div>
                </>
              )}
              <FormLines labelStyle="left" gap={1}>
                {!readOnly && <StaticField label="Name" value={formState.name.value || "-"} />}
                <StaticField label="Product Type" value={formState.lotType.value || "-"} />
                {formState.lotType.value === LotType.Bool ? (
                  <>
                    <StaticField
                      label="Customer"
                      value={customers.find((customer) => customer.id === formState.customerId.value)?.name || "-"}
                    />
                    <StaticField label="Community Type" value={formState.communityType.value || "-"} />
                  </>
                ) : (
                  <StaticField
                    label="Region"
                    value={regions.find((region) => region.id === formState.regionId.value)?.name || "-"}
                  />
                )}
              </FormLines>
            </section>
            <section>
              <div>
                <div css={Css.lg.mt4.mb3.$}>Soft Cost Invoicing Terms</div>
                <FormLines labelStyle={readOnly ? "left" : "above"} width={readOnly ? "md" : "full"}>
                  <div css={Css.df.gap2.mb1.if(readOnly).fdc.$}>
                    <BoundSelectField
                      field={formState.softCostAmountType}
                      readOnly={readOnly}
                      options={Object.values(InvoiceScheduleAmountType).map((type) => ({
                        id: type,
                        name: startCase(type),
                      }))}
                    />
                    <BoundSelectField
                      field={formState.softCostTriggerType}
                      readOnly={readOnly}
                      options={Object.values(InvoiceScheduleTriggerType).map((type) => ({ id: type, name: type }))}
                    />
                    {(formState.softCostTriggerType.value === InvoiceScheduleTriggerType.Date ||
                      formState.softCostTriggerType.value === InvoiceScheduleTriggerType.PercentageOfCompletion) && (
                      <BoundSelectField
                        field={formState.softCostDayOfMonth}
                        unsetLabel={emptyCellDash}
                        readOnly={readOnly}
                        options={daysOfMonth.map((val) => ({ id: val, name: val.toString() }))}
                      />
                    )}
                  </div>
                </FormLines>
                {formState.softCostTriggerType.value === InvoiceScheduleTriggerType.Task && (
                  <InvoiceTermsItems
                    formState={formState}
                    globalPlanMilestones={globalPlanMilestones}
                    costType={CostClassificationType.Soft}
                    readOnly={readOnly}
                  />
                )}
              </div>
              <div>
                <div css={Css.lg.mt4.mb3.$}>Hard Cost Invoicing Terms</div>
                <FormLines labelStyle={readOnly ? "left" : "above"} width={readOnly ? "md" : "full"}>
                  <div css={Css.df.gap2.mb1.if(readOnly).fdc.$}>
                    <BoundSelectField
                      field={formState.hardCostAmountType}
                      readOnly={readOnly}
                      options={Object.values(InvoiceScheduleAmountType).map((type) => ({
                        id: type,
                        name: startCase(type),
                      }))}
                    />
                    <BoundSelectField
                      field={formState.hardCostTriggerType}
                      readOnly={readOnly}
                      options={Object.values(InvoiceScheduleTriggerType).map((type) => ({ id: type, name: type }))}
                    />
                    {(formState.hardCostTriggerType.value === InvoiceScheduleTriggerType.Date ||
                      formState.hardCostTriggerType.value === InvoiceScheduleTriggerType.PercentageOfCompletion) && (
                      <BoundSelectField
                        field={formState.hardCostDayOfMonth}
                        readOnly={readOnly}
                        unsetLabel={emptyCellDash}
                        options={daysOfMonth.map((val) => ({ id: val, name: val.toString() }))}
                      />
                    )}
                  </div>
                </FormLines>
                {formState.hardCostTriggerType.value === InvoiceScheduleTriggerType.Task && (
                  <InvoiceTermsItems
                    formState={formState}
                    globalPlanMilestones={globalPlanMilestones}
                    costType={CostClassificationType.Hard}
                    readOnly={readOnly}
                  />
                )}
              </div>
            </section>
          </div>
        </>
      )}
    </Observer>
  );
}

export type InvoiceScheduleTemplateForm = SaveInvoiceScheduleTemplateInput & { createdAt: DateTime };

const formConfig: ObjectConfig<InvoiceScheduleTemplateForm> = {
  id: { type: "value" },
  name: { type: "value", rules: [required] },
  lotType: { type: "value", rules: [required] },
  customerId: { type: "value" },
  regionId: { type: "value" },
  communityType: { type: "value" },
  softCostAmountType: { type: "value", rules: [required] },
  softCostTriggerType: { type: "value", rules: [required] },
  softCostDayOfMonth: { type: "value" },
  hardCostAmountType: { type: "value", rules: [required] },
  hardCostTriggerType: { type: "value", rules: [required] },
  hardCostDayOfMonth: { type: "value" },
  createdAt: { type: "value" },
  items: {
    type: "list",
    config: {
      id: { type: "value" },
      globalPlanMilestoneId: { type: "value", rules: [required] },
      frequency: { type: "value", rules: [required] },
      costClassificationType: { type: "value", rules: [required] },
      amountInBasisPoints: { type: "value", rules: [required] },
      iterations: { type: "value", rules: [required] },
      invoiceDelayInDays: { type: "value", rules: [required] },
      delete: { type: "value" },
    },
  },
};

function mapToForm(
  invoiceTerm: InvoiceTermsForm_InvoiceScheduleTemplateFragment | InvoiceTermsFormState,
): InvoiceScheduleTemplateForm {
  // Returns loaded invoice term or the initial state provided by CreateInvoiceTermsModal
  return "id" in invoiceTerm
    ? {
        ...invoiceTerm,
        customerId: invoiceTerm.customer?.id,
        regionId: invoiceTerm.region?.id,
        communityType: invoiceTerm.communityType?.code,
        softCostAmountType: invoiceTerm.softCostAmountType.code,
        softCostTriggerType: invoiceTerm.softCostTriggerType.code,
        hardCostAmountType: invoiceTerm.hardCostAmountType.code,
        hardCostTriggerType: invoiceTerm.hardCostTriggerType.code,
        lotType: invoiceTerm.lotType.code,
        items: invoiceTerm.items.map((item) => ({
          ...item,
          costClassificationType: item.costClassificationType.code,
          frequency: item.frequency.code,
          globalPlanMilestoneId: item.globalPlanMilestone?.id,
        })),
      }
    : { ...invoiceTerm, createdAt: new Date() };
}
